/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The single source of "what day is it" for the whole UI.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * The answer comes from `GET /api/today`, which the server computes from ITS
 * system clock in the HOUSEHOLD's timezone. No component calls `new Date()` to
 * decide what today is. That is the client half of the contract in
 * server/dates.ts.
 *
 * Two refresh triggers, because a wrong "today" is invisible until someone
 * notices dinner on the wrong row:
 *
 *   1. WAKE. A laptop closed on Tuesday and opened on Thursday has a stale
 *      today. `visibilitychange` and `focus` re-fetch.
 *   2. MIDNIGHT. An app left open overnight must roll over. A timer is armed
 *      for the next LOCAL midnight — the browser clock decides *when to ask*,
 *      never *what the answer is*.
 *
 * When the server reports that the device clock disagrees by a day or more,
 * `clockWarning` carries a sentence for the UI. We surface it rather than
 * silently obeying either clock.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Meta } from "../api/endpoints.ts";
import { msUntilLocalMidnight, weekStart as calcWeekStart, type WeekStart, type YMD } from "./dates.ts";
import type { TodayPayload } from "../api/types.ts";

interface TodayContextValue {
  today: YMD | null;
  timezone: string;
  weekStartsOn: WeekStart;
  clockWarning: string | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  /** Monday (or Sunday) of the week containing `date`, per household settings. */
  weekStartFor: (date: YMD) => YMD;
}

const TodayContext = createContext<TodayContextValue | null>(null);

export function TodayProvider({ children }: { children: ReactNode }) {
  const [payload, setPayload] = useState<TodayPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  const midnightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    try {
      const next = await Meta.today();
      setError(null);
      setPayload((prev) => {
        // The day actually rolled over: everything date-scoped is now stale.
        if (prev && prev.today !== next.today) {
          void queryClient.invalidateQueries({ queryKey: ["plan"] });
          void queryClient.invalidateQueries({ queryKey: ["shopping"] });
          void queryClient.invalidateQueries({ queryKey: ["recipes"] });
          void queryClient.invalidateQueries({ queryKey: ["cook"] });
        }
        return next;
      });
    } catch (err) {
      // Keep the last known good value. A stale-by-seconds date beats a blank
      // screen, and the error is surfaced separately.
      setError(err instanceof Error ? err.message : "Couldn't reach the server.");
    } finally {
      setIsLoading(false);
    }
  }, [queryClient]);

  useEffect(() => { void load(); }, [load]);

  // ── refresh on wake ───────────────────────────────────────────────────────
  useEffect(() => {
    const onWake = () => {
      if (document.visibilityState === "visible") void load();
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    window.addEventListener("online", onWake);
    return () => {
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      window.removeEventListener("online", onWake);
    };
  }, [load]);

  // ── refresh at local midnight ─────────────────────────────────────────────
  useEffect(() => {
    const arm = () => {
      if (midnightTimer.current) clearTimeout(midnightTimer.current);
      midnightTimer.current = setTimeout(() => {
        void load();
        arm();   // re-arm for the following night
      }, msUntilLocalMidnight());
    };
    arm();
    return () => {
      if (midnightTimer.current) clearTimeout(midnightTimer.current);
    };
  }, [load]);

  const value = useMemo<TodayContextValue>(() => {
    const weekStartsOn = (payload?.weekStartsOn === 7 ? 7 : 1) as WeekStart;
    return {
      today: payload?.today ?? null,
      timezone: payload?.timezone ?? "UTC",
      weekStartsOn,
      clockWarning: payload?.clockWarning ?? null,
      isLoading,
      error,
      refresh: () => { void load(); },
      weekStartFor: (date: YMD) => calcWeekStart(date, weekStartsOn),
    };
  }, [payload, isLoading, error, load]);

  return <TodayContext.Provider value={value}>{children}</TodayContext.Provider>;
}

export function useToday(): TodayContextValue {
  const ctx = useContext(TodayContext);
  if (!ctx) throw new Error("useToday must be used inside a TodayProvider");
  return ctx;
}

/**
 * The same value, but non-null — for the many components that only render once
 * a day is known. Saves a null check in every consumer.
 */
export function useTodayValue(): YMD {
  const { today } = useToday();
  if (!today) throw new Error("useTodayValue read before the date loaded");
  return today;
}
